import { useMemo, useState } from "react";
import {
  Ban,
  KeyRound,
  MoreHorizontal,
  RotateCcw,
  Search,
  Send,
  ShieldCheck,
  Trash2,
  Users,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useSession } from "@/lib/auth-context";
import { useRoles } from "@/features/settings/use-roles";
import { useTeamUsers } from "../use-team-users";
import {
  useChangeUserRole,
  useDeleteUser,
  useResendInvite,
  useResetUserPassword,
  useRevokeInvite,
  useSetUserActive,
} from "../use-team-mutations";
import { UserStatusBadge } from "./status-badge";

function initials(name: string | null, email: string): string {
  const src = name?.trim() || email;
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] ?? "?").toUpperCase() + (parts[1]?.[0] ?? "").toUpperCase();
}

function relative(iso: string | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  return formatDistanceToNow(new Date(t), { addSuffix: true });
}

export function TeamTab() {
  const session = useSession();
  const usersQuery = useTeamUsers();
  const rolesQuery = useRoles();
  const [query, setQuery] = useState("");

  const changeRole = useChangeUserRole();
  const setActive = useSetUserActive();
  const resetPw = useResetUserPassword();
  const resend = useResendInvite();
  const revoke = useRevokeInvite();
  const deleteUser = useDeleteUser();

  const rows = usersQuery.data ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.email.toLowerCase().includes(q) ||
        (r.fullName ?? "").toLowerCase().includes(q),
    );
  }, [rows, query]);

  const pendingRoleUserId =
    changeRole.isPending && changeRole.variables ? changeRole.variables.userId : null;

  if (usersQuery.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search teammates…"
          className="h-9 pl-9"
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No teammates yet"
          description="Invite a colleague to give them access to the Colab admin."
        />
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead className="w-48">Role</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="w-32">Last active</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((u) => {
                const key = u.userId ?? u.inviteId ?? u.email;
                const isPending = u.status === "invited";
                const isSelf = u.userId && u.userId === session?.userId;
                return (
                  <TableRow key={key}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-[11px]">
                            {initials(u.fullName, u.email)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">
                            {u.fullName?.trim() || u.email}
                            {isSelf ? (
                              <span className="ml-1.5 text-[10px] text-muted-foreground">
                                (you)
                              </span>
                            ) : null}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {u.email}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {isPending ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                          <ShieldCheck className="h-3.5 w-3.5" />
                          {u.roleName ?? "Admin (default)"}
                        </span>
                      ) : (
                        <Select
                          value={u.roleId ?? undefined}
                          onValueChange={(v) =>
                            u.userId &&
                            changeRole.mutate({ userId: u.userId, roleId: v })
                          }
                          disabled={
                            !u.userId ||
                            (changeRole.isPending && pendingRoleUserId === u.userId)
                          }
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="No role" />
                          </SelectTrigger>
                          <SelectContent>
                            {(rolesQuery.data ?? []).map((r) => (
                              <SelectItem key={r.id} value={r.id} className="text-xs">
                                {r.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      <UserStatusBadge status={u.status} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {relative(u.lastSignInAt)}
                    </TableCell>
                    <TableCell>
                      <RowActions
                        isPending={isPending}
                        isSelf={!!isSelf}
                        suspended={u.status === "suspended"}
                        label={u.fullName?.trim() || u.email}
                        onReset={() =>
                          u.userId && resetPw.mutate({ userId: u.userId })
                        }
                        onToggleActive={() =>
                          u.userId &&
                          setActive.mutate({
                            userId: u.userId,
                            active: u.status === "suspended",
                          })
                        }
                        onResend={() =>
                          u.inviteId && resend.mutate({ inviteId: u.inviteId })
                        }
                        onRevoke={() =>
                          u.inviteId && revoke.mutate({ inviteId: u.inviteId })
                        }
                        onDelete={() =>
                          u.userId && deleteUser.mutate({ userId: u.userId })
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function RowActions({
  isPending,
  isSelf,
  suspended,
  label,
  onReset,
  onToggleActive,
  onResend,
  onRevoke,
  onDelete,
}: {
  isPending: boolean;
  isSelf: boolean;
  suspended: boolean;
  label: string;
  onReset: () => void;
  onToggleActive: () => void;
  onResend: () => void;
  onRevoke: () => void;
  onDelete: () => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {isPending ? (
            <>
              <DropdownMenuItem onClick={onResend}>
                <Send className="mr-2 h-4 w-4" />
                Resend invite
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onRevoke} className="text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Revoke invite
              </DropdownMenuItem>
            </>
          ) : (
            <>
              <DropdownMenuItem onClick={onReset}>
                <KeyRound className="mr-2 h-4 w-4" />
                Send password reset
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {suspended ? (
                <DropdownMenuItem onClick={onToggleActive}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Restore access
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={onToggleActive}
                  disabled={isSelf}
                  className="text-destructive"
                >
                  <Ban className="mr-2 h-4 w-4" />
                  Suspend access
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                disabled={isSelf}
                className="text-destructive"
                // Defer opening the dialog until the menu has closed to avoid
                // the focus-trap fighting between Radix popover + dialog.
                onSelect={(e) => {
                  e.preventDefault();
                  setConfirmOpen(true);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete user
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes their account and revokes all access. It
              cannot be undone — if you only need to block sign-in, use{" "}
              <span className="font-medium">Suspend access</span> instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
